import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, ArrowLeft, Loader2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { FormField } from '@/components/ui/FormField';
import { useForm } from '@/hooks/useForm';
import { loginSchema, type LoginFormData } from '@/lib/validation';

// LinkedIn Icon Component
const LinkedInIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
  </svg>
);

export default function SimpleLoginPage() {
  const navigate = useNavigate();
  const { login, linkedinLogin } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [isLinkedInLoading, setIsLinkedInLoading] = useState(false);
  const [authError, setAuthError] = useState('');

  const {
    values,
    errors,
    touched,
    isSubmitting,
    handleChange,
    handleBlur,
    handleSubmit,
  } = useForm<LoginFormData>({
    schema: loginSchema,
    initialValues: {
      email: '',
      password: '',
      rememberMe: false,
    },
    validateOnBlur: true,
    onSubmit: async (formValues) => {
      setAuthError('');
      
      try {
        const result = await login(formValues.email, formValues.password, 'member');
        
        const userRole = result?.data?.user?.role;
        if (userRole === 'admin') {
          navigate('/admin/dashboard');
        } else if (userRole === 'company') {
          navigate('/company/dashboard');
        } else if (userRole === 'cxo') {
          navigate('/cxo/dashboard');
        } else {
          navigate('/member/dashboard');
        }
      } catch (err) {
        setAuthError('Invalid email or password');
      }
    },
  });

  const handleLinkedInLogin = async () => {
    setAuthError('');
    setIsLinkedInLoading(true);
    try {
      await linkedinLogin('member');
      navigate('/member/dashboard');
    } catch (err) {
      setAuthError('LinkedIn login failed. Please try again.');
    } finally {
      setIsLinkedInLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-navy flex items-center justify-center p-4">
      {/* Back Link */}
      <Link 
        to="/" 
        className="fixed top-6 left-6 flex items-center gap-2 text-slate hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-teal rounded"
        aria-label="Back to home page"
      >
        <ArrowLeft className="w-5 h-5" aria-hidden="true" />
        <span className="text-sm">Back to home</span>
      </Link>
      
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-block focus:outline-none focus:ring-2 focus:ring-teal rounded">
            <span className="text-2xl font-bold text-white">
              CxO <span className="text-teal">On Demand</span>
            </span>
          </Link>
          <p className="text-slate mt-2">Sign in to your account</p>
        </div>

        {/* Login Form */}
        <div className="bg-surface border border-white/10 rounded-2xl p-8">
          {/* Error Alert */}
          {(authError || Object.keys(errors).length > 0) && (
            <div 
              role="alert" 
              aria-live="assertive"
              className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg"
            >
              <p className="text-sm text-red-400 text-center">
                {authError || 'Please fix the errors below'}
              </p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            <FormField
              label="Email address"
              type="email"
              value={values.email}
              onChange={(e) => handleChange('email', e.target.value)}
              onBlur={() => handleBlur('email')}
              placeholder="you@example.com"
              required
              autoComplete="email"
              error={touched.email ? errors.email : undefined}
            />

            {/* Password with visibility toggle */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-white mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={values.password}
                  onChange={(e) => handleChange('password', e.target.value)}
                  onBlur={() => handleBlur('password')}
                  placeholder="••••••••"
                  required
                  autoComplete="current-password"
                  aria-describedby={errors.password ? 'password-error' : 'password-hint'}
                  aria-invalid={touched.password && !!errors.password}
                  className={`w-full px-4 py-3 bg-navy-light border rounded-lg text-white placeholder-slate-muted focus:outline-none focus:ring-2 focus:ring-teal focus:border-transparent transition-colors ${
                    touched.password && errors.password
                      ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                      : 'border-white/10'
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-muted hover:text-white focus:outline-none focus:ring-2 focus:ring-teal rounded p-1"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" aria-hidden="true" />
                  ) : (
                    <Eye className="w-5 h-5" aria-hidden="true" />
                  )}
                </button>
              </div>
              {touched.password && errors.password && (
                <p id="password-error" className="mt-1 text-sm text-red-400">{errors.password}</p>
              )}
              <p id="password-hint" className="sr-only">
                Enter your password
              </p>
            </div>

            <div className="flex justify-between items-center">
              <label className="flex items-center gap-2 cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={values.rememberMe}
                  onChange={(e) => handleChange('rememberMe', e.target.checked)}
                  className="w-4 h-4 rounded border-white/20 bg-navy-light text-teal focus:ring-teal"
                />
                <span className="text-sm text-slate">Remember me</span>
              </label>
              <Link 
                to="/forgot-password" 
                className="text-sm text-teal hover:underline focus:outline-none focus:ring-2 focus:ring-teal rounded"
              >
                Forgot password?
              </Link>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              aria-busy={isSubmitting}
              className="w-full py-3.5 bg-teal text-navy font-semibold rounded-lg hover:bg-teal-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 focus:outline-none focus:ring-2 focus:ring-teal focus:ring-offset-2 focus:ring-offset-navy"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" />
                  Signing in...
                </>
              ) : (
                'Sign in'
              )}
            </button>
          </form>

          {/* Divider */}
          <div className="relative my-6 text-center">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-white/10"></div>
            </div>
            <span className="relative bg-surface px-3 text-sm text-slate">Or</span>
          </div>

          {/* LinkedIn Login */}
          <button
            type="button"
            onClick={handleLinkedInLogin}
            disabled={isLinkedInLoading}
            aria-busy={isLinkedInLoading}
            className="w-full flex items-center justify-center gap-3 py-3.5 bg-[#0A66C2] text-white font-medium rounded-lg hover:bg-[#0958a8] transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-[#0A66C2] focus:ring-offset-2 focus:ring-offset-navy"
          >
            <LinkedInIcon />
            <span>{isLinkedInLoading ? 'Connecting...' : 'Sign in with LinkedIn'}</span>
          </button>
        </div>

        {/* Sign Up Link */}
        <p className="text-center mt-6 text-slate">
          Don't have an account?{' '}
          <Link 
            to="/register" 
            className="text-teal hover:underline focus:outline-none focus:ring-2 focus:ring-teal rounded"
          >
            Sign up
          </Link>
        </p>
      </div>
    </div>
  );
}
